<script setup lang="ts">
// Maintenance panel, opened by a long press on the logo plus the four digit code.
// Held by the WeAreDevelopers team, not by the people on shift.
//
// An overlay rather than its own address on purpose. A phone sitting in a
// semi-public place should not carry a typeable route to an admin screen, and an
// overlay also cannot flash into view before a guard sends it away.
const emit = defineEmits<{ close: [] }>()

const device = useScanDevice()
const nativeInfo = useNativeInfo()
// The app's own answer rather than navigator.onLine, which inside the APK reports a
// phone in airplane mode as online. Read here so this panel and a support request
// cannot disagree about the same phone at the same moment.
const online = useOnline()
const { pending: pendingSupport, refreshPending } = useSupportRequest()
const {
  pending: pendingCount,
  synced: syncedCount,
  rejected: rejectedCount,
  unrecorded: unrecordedCount,
  today: todayCount,
  stranded: strandedCount,
  refresh,
  wipe,
} = useScanQueue()

useModalBehaviour(() => emit('close'))

const storage = ref<{ usage: number, quota: number } | null>(null)

// Enough to cover the last few minutes at a busy door, short enough to be read rather
// than scrolled.
const RECENT_LIMIT = 20

const recent = ref<StoredScan[]>([])

// What a wipe would delete: every scan on the phone, not only this pairing's.
const total = ref(0)

const loadScans = async () => {
  await refresh()
  recent.value = await recentScans(device.value.pairingId, RECENT_LIMIT)
  total.value = await countAll()
  storage.value = await storageEstimate()
}

onMounted(() => {
  refreshPending()
  loadScans()
})

// One row per scan, lead and door together: it is one phone. The badge reference is the
// tail of the code and never a name.
const rows = computed(() => recent.value.map(scan => ({
  uuid: scan.uuid,
  time: eventClock(scan.scannedAt, device.value.eventTimeZone),
  ...summariseScan(scan),
})))

// Shown as a percentage because the raw byte counts mean nothing to whoever is
// reading this on a hall floor.
const storageUsedPercent = computed(() => {
  if (!storage.value?.quota) {
    return null
  }

  return Math.round(storage.value.usage / storage.value.quota * 100)
})

const copied = ref(false)

const diagnostics = computed(() => ({
  device_label: device.value.deviceLabel,
  partner: device.value.partnerName,
  area: device.value.areaName,
  // The door, on a phone that has one, so a copied diagnostic says which kind it is.
  session: device.value.sessionTitle,
  provisioned: device.value.provisioned,
  pending: pendingCount.value,
  synced: syncedCount.value,
  rejected: rejectedCount.value,
  unrecorded: unrecordedCount.value,
  stranded: strandedCount.value,
  scanned_today: todayCount.value,
  support_pending: pendingSupport.value,
  online: online.value,
  storage_usage_bytes: storage.value?.usage ?? null,
  storage_quota_bytes: storage.value?.quota ?? null,
  platform: nativeInfo.value.platform,
  app_build: nativeInfo.value.build,
  user_agent: navigator.userAgent,
}))

const copyDiagnostics = async () => {
  await navigator.clipboard.writeText(JSON.stringify(diagnostics.value, null, 2))
  copied.value = true
  setTimeout(() => {
    copied.value = false
  }, 2000)
}

// The end of day upload, and the way out of a phone whose queue never drained.
const { drain } = useScanUpload()
const uploading = ref(false)
const uploadResult = ref('')

const forceSync = async () => {
  if (uploading.value) {
    return
  }

  uploading.value = true
  uploadResult.value = ''

  const { sent, remaining } = await drain()

  uploading.value = false
  uploadResult.value = remaining > 0
    ? `Sent ${sent}, ${remaining} still waiting. Check the connection.`
    : `Sent ${sent}. Nothing left waiting.`

  // Reread, so the list shows what has moved.
  await loadScans()
}

// Records left behind by pairings this phone has since left, whatever became of them.
// The stranded count above covers only the ones still waiting to be sent.
const otherPairings = computed(() => Math.max(
  0,
  total.value
  - (pendingCount.value + syncedCount.value + rejectedCount.value + unrecordedCount.value),
))

// A second tap, like the wipe below, though this one cannot reach the current pairing's
// scans. Clearing early also leaves one less partner's data on a shared phone.
const confirmingClear = ref(false)

const clearOtherPairings = async () => {
  if (!confirmingClear.value) {
    confirmingClear.value = true

    return
  }

  await wipeOtherPairings(device.value.pairingId)
  await loadScans()
  confirmingClear.value = false
}

// Wiping needs a second tap. Nothing else in the app deletes stored scans, and one
// tap in the wrong place would throw away a shift's work.
const confirmingWipe = ref(false)

const wipeLocalData = async () => {
  if (!confirmingWipe.value) {
    confirmingWipe.value = true

    return
  }

  await wipe()
  await loadScans()
  confirmingWipe.value = false
}

// Logging out takes the key with it, and uploading needs that key. Anything still
// waiting would be stranded on the phone with no way of ever reaching us, so the
// button is refused until the queues are empty.
const waiting = computed(() => pendingCount.value + pendingSupport.value)
const canSignOut = computed(() => waiting.value === 0)

// Unpairing also needs a second tap. The setup code was cleared when it was used,
// which means a phone unpaired by accident cannot be put back without a new code
// from us, and during an event that leaves an area unable to scan.
const confirmingSignOut = ref(false)

const signOut = () => {
  if (!canSignOut.value) {
    return
  }

  if (!confirmingSignOut.value) {
    confirmingSignOut.value = true

    return
  }

  device.value = emptyScanDevice()
  emit('close')
  navigateTo('/')
}
</script>

<template>
  <div
    class="maintenance"
    role="dialog"
    aria-modal="true"
    aria-label="Maintenance"
    @click.self="emit('close')"
  >
    <div class="maintenance__panel">
      <header class="maintenance__header">
        <h2 class="maintenance__title">Maintenance</h2>
        <button type="button" class="maintenance__close" @click="emit('close')">Close</button>
      </header>

      <div class="maintenance__body">
        <section class="maintenance__section">
          <h3 class="maintenance__heading">This device</h3>
          <dl class="maintenance__list">
            <div>
              <dt>Partner</dt>
              <dd>{{ device.partnerName || 'Not set up' }}</dd>
            </div>
            <div>
              <dt>Device</dt>
              <dd>{{ device.deviceLabel || 'Not set up' }}</dd>
            </div>
            <!-- Whichever of the two this phone was handed out to do. A door phone has
                 no area, and "None selected" against it read as a fault. -->
            <div v-if="device.purpose === 'access_control'">
              <dt>Door</dt>
              <dd>{{ device.sessionTitle || 'None selected' }}</dd>
            </div>
            <div v-else>
              <dt>Area</dt>
              <dd>{{ device.areaName || 'None selected' }}</dd>
            </div>
            <div>
              <dt>Key</dt>
              <dd>{{ device.provisioned ? 'Present' : 'Missing' }}</dd>
            </div>
          </dl>
        </section>

        <section class="maintenance__section">
          <h3 class="maintenance__heading">Scans on this phone</h3>
          <dl class="maintenance__list">
            <div>
              <dt>Waiting to upload</dt>
              <dd>{{ pendingCount }}</dd>
            </div>
            <div>
              <dt>Uploaded</dt>
              <dd>{{ syncedCount }}</dd>
            </div>
            <div>
              <dt>Rejected</dt>
              <dd>{{ rejectedCount }}</dd>
            </div>
            <!-- Only when there are any: a lead phone never has them. Door checks with
                 nothing to send, kept out of the waiting count that must reach zero. -->
            <div v-if="unrecordedCount">
              <dt>Not sent, nothing to send</dt>
              <dd>{{ unrecordedCount }}</dd>
            </div>
            <!-- Only when there are any, and worth saying plainly when there are. These
                 belong to a partner this phone no longer works for, so no upload will
                 ever clear them and nobody should wait for one. -->
            <div v-if="strandedCount">
              <dt>From a previous pairing</dt>
              <dd>{{ strandedCount }} waiting, cannot be sent</dd>
            </div>
            <!-- Answers "is this phone doing anything", where the rest answer "is
                 anything stuck". -->
            <div>
              <dt>Scanned today</dt>
              <dd>{{ todayCount }}</dd>
            </div>
            <!-- A support message that never left the phone is worth seeing here,
                 since nothing else in the app mentions it after it was written. -->
            <div>
              <dt>Support messages waiting</dt>
              <dd>{{ pendingSupport }}</dd>
            </div>
            <!-- A phone approaching its limit needs noticing before it starts
                 failing to save scans. -->
            <div v-if="storageUsedPercent !== null">
              <dt>Storage used</dt>
              <dd>{{ storageUsedPercent }}%</dd>
            </div>
          </dl>

          <!-- What the counts are made of: "2 waiting" does not say which two. Also the
               quickest way to check that the badge just scanned registered. -->
          <h4 class="maintenance__heading maintenance__heading--sub">Latest scans</h4>

          <p v-if="!rows.length" class="maintenance__note">
            Nothing scanned on this phone yet.
          </p>

          <ul v-else class="maintenance__scans">
            <li v-for="row in rows" :key="row.uuid" class="maintenance__scan">
              <span class="maintenance__scan-time">{{ row.time }}</span>
              <span class="maintenance__scan-badge">{{ row.badge }}</span>
              <span class="maintenance__scan-result">{{ row.result }}</span>
              <span class="maintenance__scan-state">{{ row.state }}</span>
            </li>
          </ul>
        </section>

        <section class="maintenance__actions">
          <button
            type="button"
            class="scan-option"
            :disabled="uploading"
            @click="forceSync"
          >
            {{ uploading ? 'Uploading' : 'Upload now' }}
          </button>

          <p v-if="uploadResult" class="maintenance__note" role="status" aria-live="polite">
            {{ uploadResult }}
          </p>
          <button type="button" class="scan-option" @click="copyDiagnostics">
            {{ copied ? 'Copied' : 'Copy diagnostics' }}
          </button>
          <!-- Only when an earlier pairing left something behind. Not styled as a
               danger: it cannot reach the scans this partner is collecting. -->
          <button
            v-if="otherPairings"
            type="button"
            class="scan-option"
            @click="clearOtherPairings"
          >
            {{ confirmingClear ? 'Tap again to clear' : 'Clear scans from a previous pairing' }}
          </button>

          <p v-if="confirmingClear" class="maintenance__warning" role="status" aria-live="polite">
            This deletes {{ otherPairings }} scans left by an earlier pairing. Scans taken
            for {{ device.partnerName || 'this partner' }} are not touched.
          </p>

          <!-- Destructive and deliberately last. Nothing else in the app deletes
               stored scans. -->
          <button type="button" class="scan-option maintenance__danger" @click="wipeLocalData">
            {{ confirmingWipe ? 'Tap again to wipe' : 'Wipe local data' }}
          </button>

          <p v-if="confirmingWipe" class="maintenance__warning" role="status" aria-live="polite">
            This deletes all {{ total }} scans on this phone, including any from an
            earlier pairing. It cannot be undone.
          </p>

          <button
            type="button"
            class="scan-option maintenance__danger"
            :disabled="!canSignOut"
            @click="signOut"
          >
            {{ confirmingSignOut ? 'Tap again to log out' : 'Log out of this phone' }}
          </button>

          <p v-if="!canSignOut" class="maintenance__warning" role="status" aria-live="polite">
            Upload the {{ waiting }} waiting
            {{ waiting === 1 ? 'item' : 'items' }} first. Logging out now would leave
            them stuck on this phone.
          </p>
          <p
            v-else-if="confirmingSignOut"
            class="maintenance__warning"
            role="status"
            aria-live="polite"
          >
            This phone will need a new setup code before it can scan again.
          </p>
        </section>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.maintenance {
  position: fixed;
  inset: 0;
  z-index: 110;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.6);

  &__panel {
    width: 100%;
    max-width: 480px;
    max-height: 92dvh;
    overflow-y: auto;
    padding: 0;
    background-color: $wad4-c-background-bg-primary;
    border-radius: $wad4-border-radius-lg $wad4-border-radius-lg 0 0;
  }
  &__header {
    position: sticky;
    top: 0;
    z-index: 1;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    padding: $wad4-spacing-4;
    background-color: $wad4-c-background-bg-primary;
    border-bottom: 1px solid $wad4-c-border-border-primary;
  }

  // Carries the spacing the panel no longer has.
  &__body {
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-5;
    padding: $wad4-spacing-4;
    padding-bottom: max(#{$wad4-spacing-4}, env(safe-area-inset-bottom));
  }

  &__title {
    margin: 0;
    @include title-display-mixin('xs');
  }

  &__close {
    padding: $wad4-spacing-2;
    font-family: $wad4-default-font-sans;
    @include text-size-mixin('sm');
    color: $wad4-c-text-text-secondary;
    background: none;
    border: none;
    cursor: pointer;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-2;
  }

  &__heading {
    margin: 0;
    @include text-size-mixin('sm');
    font-weight: 400;
    color: $wad4-c-text-text-secondary;
  }

  &__list {
    display: flex;
    flex-direction: column;
    margin: 0;

    > div {
      display: flex;
      justify-content: space-between;
      gap: $wad4-spacing-3;
      padding: $wad4-spacing-2 0;
      border-bottom: 1px solid $wad4-c-border-border-primary;
    }

    dt {
      color: $wad4-c-text-text-secondary;
    }

    dd {
      margin: 0;
      font-weight: 500;
      text-align: right;
      word-break: break-word;
    }
  }

  // A label on part of a section, not a section of its own.
  &__heading--sub {
    margin-top: $wad4-spacing-2;
  }

  // Bounded, so twenty rows cannot push the buttons below the fold.
  &__scans {
    max-height: 14rem;
    overflow-y: auto;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  // Time and badge reference fixed, result takes what is left, upload state at the
  // right edge where the counts above it sit.
  &__scan {
    display: grid;
    grid-template-columns: auto auto 1fr auto;
    gap: $wad4-spacing-2;
    align-items: baseline;
    padding: $wad4-spacing-2 0;
    @include text-size-mixin('sm');
    border-bottom: 1px solid $wad4-c-border-border-primary;
  }

  // Monospaced like the door screen: references to check, not words to read.
  &__scan-time,
  &__scan-badge {
    font-family: $wad4-default-font-mono;
    color: $wad4-c-text-text-secondary;
  }

  &__scan-result {
    min-width: 0;
    word-break: break-word;
  }

  &__scan-state {
    color: $wad4-c-text-text-secondary;
    white-space: nowrap;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-2;
  }

  &__danger {
    color: $wad4-bc-error-600;

    &:disabled {
      color: $wad4-c-text-text-secondary;
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  &__warning {
    margin: 0;
    @include text-size-mixin('sm');
    color: $wad4-bc-error-600;
  }

  &__note {
    margin: 0;
    @include text-size-mixin('sm');
    color: $wad4-c-text-text-secondary;
  }
}
</style>
